import { useEffect, useId, useRef, useState } from "react";
import { WaveBlockKind } from "@vida/wave-core";
import { WaveCommandBar } from "./WaveCommandBar";
import { WaveWidgetPicker } from "./WaveWidgetPicker";
import { WAVE_FEED_NODE_SIZE } from "./waveFeedTokens";

type Props = {
  commandText: string;
  onCommandChange: (value: string) => void;
  onAddPlainText: () => void;
  onAddAssistant: () => void;
  onPickFile: () => void;
  onInsertBlock: (kind: WaveBlockKind) => void;
  busy?: boolean;
};

export function WaveTimelineComposer({
  commandText,
  onCommandChange,
  onAddPlainText,
  onAddAssistant,
  onPickFile,
  onInsertBlock,
  busy = false,
}: Props) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const pickerId = useId();
  const showSlashMenu = commandText.startsWith("/");

  useEffect(() => {
    if (!pickerOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      if (pickerRef.current?.contains(e.target as Node)) return;
      setPickerOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPickerOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [pickerOpen]);

  const closePicker = () => setPickerOpen(false);

  const wrapInsert = (kind: WaveBlockKind) => {
    onInsertBlock(kind);
    onCommandChange("");
    closePicker();
  };

  const submitComposer = () => {
    if (busy || commandText.startsWith("/")) return;
    if (
      commandText.toLowerCase().includes("@assistente") ||
      commandText.toLowerCase().includes("@documenti:")
    ) {
      onAddAssistant();
    } else {
      onAddPlainText();
    }
  };

  return (
    <div className="wave-timeline-composer">
      {showSlashMenu && (
        <div className="wave-timeline-composer-slash">
          <WaveCommandBar onInsert={(kind) => wrapInsert(kind)} />
        </div>
      )}

      <div className="wave-timeline-composer-row wave-feed-grid">
        <div className="wave-feed-grid-gutter wave-timeline-composer-gutter" aria-hidden />

        <div className="wave-feed-grid-main wave-timeline-composer-main">
          <div className="wave-timeline-composer-field-row" ref={pickerRef}>
            <button
              type="button"
              className="wave-timeline-composer-plus"
              disabled={busy}
              aria-expanded={pickerOpen}
              aria-controls={pickerId}
              aria-label="Aggiungi widget"
              onClick={() => setPickerOpen((v) => !v)}
            >
              +
            </button>

            {pickerOpen && (
              <div id={pickerId} className="wave-timeline-composer-picker">
                <WaveWidgetPicker
                  commandText={commandText}
                  onAddPlainText={() => {
                    closePicker();
                    onAddPlainText();
                  }}
                  onAddAssistant={() => {
                    closePicker();
                    onAddAssistant();
                  }}
                  onPickFile={() => {
                    closePicker();
                    onPickFile();
                  }}
                  onInsertBlock={(kind) => wrapInsert(kind)}
                />
              </div>
            )}

            <input
              className="wave-timeline-composer-input"
              value={commandText}
              disabled={busy}
              placeholder="Aggiungi widget o scrivi qualcosa…"
              onChange={(e) => onCommandChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  submitComposer();
                }
              }}
            />
          </div>
        </div>

        <div className="wave-feed-grid-rail wave-timeline-composer-rail">
          <span
            className="wave-timeline-composer-rail-node"
            style={{ width: WAVE_FEED_NODE_SIZE, height: WAVE_FEED_NODE_SIZE }}
            aria-hidden
          />
        </div>
      </div>
    </div>
  );
}
